import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { DeliverySlotException, AvailableDeliverySlots } from '../../client/services';
import Iconify from '../../components/Iconify';
import DeliveryTimeSlotsEditor, {
  parseDeliveryTimeSlotsValue,
  stringifyDeliveryTimeSlots,
  getDeliverySlotsOverlapMessage,
  MAX_DELIVERY_TIME_SLOTS,
} from '../Settings/DeliveryTimeSlotsEditor';
import OrderGapSettings from './OrderGapSettings';

const WEEKDAYS = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

type SlotRow = { slot: string; maxPoints: number };

type WeekdayRule = {
  id: string;
  weekday: number;
  effectiveFrom: string;
  slots: SlotRow[];
};

type DateRule = {
  id: string;
  date: string;
  slots: SlotRow[];
};

type RulesState = {
  weekdays: WeekdayRule[];
  dates: DateRule[];
};

const emptyRules = (): RulesState => ({
  weekdays: [],
  dates: [],
});

const readRules = (result: any): RulesState => ({
  weekdays: result?.weekdays || [],
  dates: result?.dates || [],
});

type CapacityNote = {
  date: string;
  slot: string;
  booked: number;
  shown: number;
};

const slotKey = (label: string): string =>
  String(label || '').trim().replace(/\s+/g, ' ').toLowerCase();

const nextWeekdayIso = (weekday: number, fromIso: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const from = toLocalDate(fromIso);
  const cursor = new Date(from > today ? from : today);
  for (let step = 0; step < 7; step += 1) {
    if (cursor.getDay() === weekday) return toIsoDate(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  return toIsoDate(cursor);
};

type UsageSlot = {
  slot: string;
  usedPoints: number;
};

const SlotCards = ({
  slots,
  usage,
  pointsOnly = false,
}: {
  slots: SlotRow[];
  usage?: UsageSlot[];
  pointsOnly?: boolean;
}) => (
  <Stack direction='row' spacing={1.25} flexWrap='wrap' useFlexGap sx={{ width: '100%' }}>
    {(slots || []).map((slot) => {
      const total = Number(slot.maxPoints) || 0;
      const booked =
        (usage || []).find((item) => slotKey(item.slot) === slotKey(slot.slot))
          ?.usedPoints || 0;
      const left = Math.max(total - booked, 0);
      const title = String(slot.slot || '').replace(/\s+/g, ' ').trim();
      if (pointsOnly) {
        return (
          <Box
            key={title}
            sx={{
              flex: '1 1 200px',
              minWidth: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 1.5,
              py: 0.85,
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'rgba(238, 55, 80, 0.12)',
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: 'text.primary' }}>
              {title}
            </Typography>
            <Box
              sx={{
                flexShrink: 0,
                minWidth: 54,
                px: 1,
                py: 0.35,
                borderRadius: 999,
                bgcolor: 'primary.lighter',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.1, color: 'primary.main' }}>
                {total}
              </Typography>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  lineHeight: 1.1,
                  color: 'primary.main',
                  textTransform: 'uppercase',
                }}
              >
                Points
              </Typography>
            </Box>
          </Box>
        );
      }
      return (
        <Box
          key={title}
          sx={{
            flex: '1 1 220px',
            maxWidth: 260,
            minWidth: 220,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }} noWrap>
            {title}
          </Typography>
          <Stack direction='row' justifyContent='space-between' sx={{ width: '100%' }}>
            {[
              ['Total', total],
              ['Booked', booked],
              ['Left', left],
            ].map(([label, value]) => (
              <Box key={label} sx={{ minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.2 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      );
    })}
  </Stack>
);

const toLocalDate = (iso: string): Date => {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const toIsoDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatTableDate = (iso: string): string => {
  const date = toLocalDate(iso);
  return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const slotListSignature = (json: string): string => {
  try {
    const parsed = JSON.parse(json || '[]');
    if (!Array.isArray(parsed)) return '';
    return parsed
      .map((item) => {
        const slot = String(item?.slot || '')
          .trim()
          .replace(/\s+/g, ' ')
          .toLowerCase();
        const points = Number(item?.maxPoints) || 0;
        return `${slot}|${points}`;
      })
      .sort()
      .join(';');
  } catch {
    return '';
  }
};

const sameSlotList = (left: string, right: string): boolean =>
  slotListSignature(left) === slotListSignature(right);

type Props = {
  defaultSlotsJson: string;
  onSaved: () => void;
};

const DeliverySlotExceptions = ({ defaultSlotsJson, onSaved }: Props) => {
  const [mode, setMode] = useState<'weekdays' | 'date'>('weekdays');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [date, setDate] = useState('');
  const [draft, setDraft] = useState(
    defaultSlotsJson && defaultSlotsJson !== '[]' ? defaultSlotsJson : '[]'
  );
  const [editorKey, setEditorKey] = useState(0);
  const [slotsCleared, setSlotsCleared] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rules, setRules] = useState<RulesState>(emptyRules);
  const [viewTab, setViewTab] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const todayIso = toIsoDate(new Date());
  const [selectedIso, setSelectedIso] = useState(todayIso);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [usageByDate, setUsageByDate] = useState<Record<string, UsageSlot[]>>({});
  const [datePage, setDatePage] = useState(0);
  const [datesPerPage, setDatesPerPage] = useState(25);
  const [editingRule, setEditingRule] = useState<DateRule | null>(null);
  const [editDraft, setEditDraft] = useState('[]');
  const [editKey, setEditKey] = useState(0);
  const [editSaving, setEditSaving] = useState(false);

  const loadRules = async () => {
    const result = await DeliverySlotException.find();
    setRules(readRules(result));
  };

  useEffect(() => {
    loadRules().catch(() => {
      setRules(emptyRules());
    });
  }, []);

  useEffect(() => {
    const dates = new Set<string>([selectedIso]);
    rules.dates.forEach((rule) => dates.add(String(rule.date).slice(0, 10)));
    rules.weekdays.forEach((rule) => {
      dates.add(nextWeekdayIso(rule.weekday, String(rule.effectiveFrom)));
    });

    let cancelled = false;
    Promise.all(
      Array.from(dates).map(async (date) => {
        try {
          const result = await AvailableDeliverySlots.find({
            query: { date, points: 0, acceptance: false },
          });
          const slots = (result?.slots || []).map((slot: any) => ({
            slot: String(slot.slot || ''),
            usedPoints: Number(slot.usedPoints) || 0,
          }));
          return [date, slots] as const;
        } catch {
          return [date, []] as const;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setUsageByDate(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [rules, selectedIso]);

  const resetDraft = () => {
    const daily = defaultSlotsJson && defaultSlotsJson !== '[]' ? defaultSlotsJson : '[]';
    setDraft(daily);
    setEditorKey((current) => current + 1);
    setSlotsCleared(false);
    setWeekdays([]);
    setDate('');
  };

  const dailySlotsJson =
    defaultSlotsJson && defaultSlotsJson !== '[]' ? defaultSlotsJson : '[]';

  const loadEditor = (json: string) => {
    setDraft(json);
    setEditorKey((current) => current + 1);
    setSlotsCleared(json === '[]');
  };

  const weekdaySlotsJson = (weekday: number): string => {
    const rule = rules.weekdays
      .filter((item) => Number(item.weekday) === Number(weekday))
      .sort((left, right) =>
        String(right.effectiveFrom).localeCompare(String(left.effectiveFrom))
      )[0];
    if (rule) return JSON.stringify(rule.slots || []);
    return dailySlotsJson;
  };

  const dateSlotsJson = (iso: string): string => {
    const dateRule = rules.dates.find((rule) => String(rule.date).slice(0, 10) === iso);
    if (dateRule) return JSON.stringify(dateRule.slots || []);
    const weekday = toLocalDate(iso).getDay();
    const weekdayRule = rules.weekdays
      .filter(
        (rule) =>
          rule.weekday === weekday && String(rule.effectiveFrom).slice(0, 10) <= iso
      )
      .sort((left, right) =>
        String(right.effectiveFrom).localeCompare(String(left.effectiveFrom))
      )[0];
    if (weekdayRule) return JSON.stringify(weekdayRule.slots || []);
    return dailySlotsJson;
  };

  const selectWeekday = (value: number) => {
    if (weekdays.length === 1 && weekdays[0] === value) return;
    setWeekdays([value]);
    loadEditor(weekdaySlotsJson(value));
  };

  const selectDate = (iso: string) => {
    if (!iso) {
      setDate('');
      return;
    }
    setDate(iso);
    loadEditor(dateSlotsJson(iso));
  };

  const openWeekdayForm = () => {
    resetDraft();
    setMode('weekdays');
    setWeekdays([0]);
    loadEditor(weekdaySlotsJson(0));
    setFormOpen(true);
  };

  const openWeekdayEditor = (weekdayValue: number) => {
    resetDraft();
    setMode('weekdays');
    setWeekdays([weekdayValue]);
    loadEditor(weekdaySlotsJson(weekdayValue));
    setFormOpen(true);
  };

  const openDateForm = () => {
    resetDraft();
    setMode('date');
    setDate(todayIso);
    loadEditor(dateSlotsJson(todayIso));
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetDraft();
  };

  const handleDraftChange = (next: string) => {
    if (next === '[]') {
      if (draft !== '[]') setSlotsCleared(true);
    } else {
      setSlotsCleared(false);
    }
    setDraft(next);
  };

  const handleSave = async () => {
    const rows = parseDeliveryTimeSlotsValue(draft);
    const hasIncomplete = rows.some(
      (row) =>
        (row.startTime || row.endTime || row.maxPoints) &&
        (!row.startTime || !row.endTime || !row.maxPoints)
    );
    if (hasIncomplete) {
      toast.error('Please complete start time, end time, and total points');
      return;
    }

    const overlapMessage = getDeliverySlotsOverlapMessage(rows);
    if (overlapMessage) {
      toast.error(overlapMessage);
      return;
    }

    const jsonValue = stringifyDeliveryTimeSlots(rows);
    const disabling = jsonValue === '[]';

    let savedSlots: unknown[] = [];
    try {
      savedSlots = JSON.parse(jsonValue);
    } catch {
      savedSlots = [];
    }
    if (savedSlots.length > MAX_DELIVERY_TIME_SLOTS) {
      toast.error(`Maximum ${MAX_DELIVERY_TIME_SLOTS} time slots allowed`);
      return;
    }

    if (mode === 'weekdays' && weekdays.length === 0) {
      toast.error('Choose at least one weekday');
      return;
    }
    if (mode === 'date' && !date) {
      toast.error('Choose a date');
      return;
    }

    const unchanged =
      mode === 'date'
        ? sameSlotList(jsonValue, dateSlotsJson(date))
        : weekdays.every((weekday) => sameSlotList(jsonValue, weekdaySlotsJson(weekday)));
    if (unchanged) {
      toast.info('Already the same. No new day slots were added.');
      return;
    }

    setIsSaving(true);
    try {
      const response = await DeliverySlotException.create({
        weekdays: mode === 'weekdays' ? weekdays : [],
        date: mode === 'date' ? date : '',
        slots: savedSlots,
      });
      setRules(readRules(response));
      const notes = (response?.notes || []) as CapacityNote[];
      if (disabling) {
        toast.warning('This day is disabled.');
      }
      if (notes.length > 0) {
        const preview = notes
          .slice(0, 4)
          .map(
            (note) =>
              `${note.date} ${note.slot}: ${note.booked} already booked, showing ${note.shown}`
          )
          .join(' · ');
        toast.warning(
          notes.length > 4
            ? `${preview} · and ${notes.length - 4} more`
            : preview
        );
      } else if (!disabling) {
        toast.success('Day slots saved');
      }
      resetDraft();
      setFormOpen(false);
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save day slots');
    } finally {
      setIsSaving(false);
    }
  };

  const dateRuleByIso = useMemo(() => {
    const map = new Map<string, DateRule>();
    rules.dates.forEach((rule) => map.set(String(rule.date).slice(0, 10), rule));
    return map;
  }, [rules.dates]);

  const weekdayRuleFor = (iso: string): WeekdayRule | undefined => {
    const weekday = toLocalDate(iso).getDay();
    return rules.weekdays
      .filter(
        (rule) =>
          rule.weekday === weekday && String(rule.effectiveFrom).slice(0, 10) <= iso
      )
      .sort((a, b) =>
        String(b.effectiveFrom).localeCompare(String(a.effectiveFrom))
      )[0];
  };

  const calendarCells = useMemo(() => {
    const year = visibleMonth.getFullYear();
    const month = visibleMonth.getMonth();
    const leading = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    return [
      ...Array.from({ length: leading }, () => null),
      ...Array.from({ length: days }, (_, index) => index + 1),
    ];
  }, [visibleMonth]);

  const selectedDateRule = dateRuleByIso.get(selectedIso);
  const selectedWeekdayRule = selectedDateRule ? undefined : weekdayRuleFor(selectedIso);
  const defaultSlots = useMemo(() => {
    try {
      const parsed = JSON.parse(defaultSlotsJson || '[]');
      return Array.isArray(parsed) ? (parsed as SlotRow[]) : [];
    } catch {
      return [];
    }
  }, [defaultSlotsJson]);
  const selectedSlots = selectedDateRule
    ? selectedDateRule.slots || []
    : selectedWeekdayRule?.slots || defaultSlots;

  const sortedDateRules = useMemo(
    () =>
      [...rules.dates].sort((left, right) =>
        String(left.date).localeCompare(String(right.date))
      ),
    [rules.dates]
  );
  const datePageSafe = Math.min(
    datePage,
    Math.max(0, Math.ceil(sortedDateRules.length / datesPerPage) - 1)
  );
  const pagedDateRules = sortedDateRules.slice(
    datePageSafe * datesPerPage,
    datePageSafe * datesPerPage + datesPerPage
  );

  const handleDelete = async (id: string, kind: 'date' | 'weekday' = 'weekday') => {
    try {
      const response = await DeliverySlotException.remove(id);
      setRules(readRules(response));
      toast.success(
        kind === 'date'
          ? 'This date was removed. Weekday slots apply again.'
          : 'Weekday slots removed. Past days stay as they were.'
      );
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove day slots');
    }
  };

  const openDateEditor = (rule: DateRule) => {
    setEditingRule(rule);
    setEditDraft(JSON.stringify(rule.slots || []));
    setEditKey((current) => current + 1);
  };

  const handleEditSave = async () => {
    if (!editingRule) return;
    const rows = parseDeliveryTimeSlotsValue(editDraft);
    const hasIncomplete = rows.some(
      (row) =>
        (row.startTime || row.endTime || row.maxPoints) &&
        (!row.startTime || !row.endTime || !row.maxPoints)
    );
    if (hasIncomplete) {
      toast.error('Please complete start time, end time, and total points');
      return;
    }
    const overlapMessage = getDeliverySlotsOverlapMessage(rows);
    if (overlapMessage) {
      toast.error(overlapMessage);
      return;
    }
    const jsonValue = stringifyDeliveryTimeSlots(rows);
    let savedSlots: unknown[] = [];
    try {
      savedSlots = JSON.parse(jsonValue);
    } catch {
      savedSlots = [];
    }
    if (savedSlots.length > MAX_DELIVERY_TIME_SLOTS) {
      toast.error(`Maximum ${MAX_DELIVERY_TIME_SLOTS} time slots allowed`);
      return;
    }
    if (
      jsonValue !== '[]' &&
      sameSlotList(JSON.stringify(editingRule.slots || []), jsonValue)
    ) {
      toast.info('Already the same. No new day slots were added.');
      return;
    }

    setEditSaving(true);
    try {
      const response = await DeliverySlotException.create({
        weekdays: [],
        date: String(editingRule.date).slice(0, 10),
        slots: savedSlots,
      });
      setRules(readRules(response));
      toast.success(jsonValue === '[]' ? 'This day is disabled.' : 'Day slots saved');
      setEditingRule(null);
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save day slots');
    } finally {
      setEditSaving(false);
    }
  };

  const slotsOpen = mode === 'weekdays' ? weekdays.length > 0 : Boolean(date);

  return (
    <Card sx={{ pt: { xs: 1.5, md: 2 }, px: { xs: 1.5, sm: 2, md: 3 }, pb: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0 }}>
      <Stack spacing={0}>
        <Stack spacing={2} sx={{ order: 1, mt: 0 }}>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Slot schedule
            </Typography>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems='flex-start'
            >
              <Box
                sx={{
                  flex: { md: '0 1 460px' },
                  width: { xs: '100%', md: 'auto' },
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1.5 }}>
                  <IconButton
                    aria-label='Previous month'
                    onClick={() =>
                      setVisibleMonth(
                        (current) => new Date(current.getFullYear(), current.getMonth() - 1, 1)
                      )
                    }
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography sx={{ fontWeight: 700 }}>
                    {visibleMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                  </Typography>
                  <IconButton
                    aria-label='Next month'
                    onClick={() =>
                      setVisibleMonth(
                        (current) => new Date(current.getFullYear(), current.getMonth() + 1, 1)
                      )
                    }
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    textAlign: 'center',
                    mb: 0.5,
                  }}
                >
                  {WEEKDAY_LETTERS.map((letter, index) => (
                    <Typography key={`${letter}-${index}`} variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                      {letter}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 0.5 }}>
                  {calendarCells.map((day, index) => {
                    if (!day) {
                      return <Box key={`empty-${index}`} sx={{ minHeight: 48 }} />;
                    }
                    const iso = toIsoDate(
                      new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day)
                    );
                    const selected = iso === selectedIso;
                    const isToday = iso === todayIso;
                    const oneDate = dateRuleByIso.has(iso);
                    const weekly = !oneDate && Boolean(weekdayRuleFor(iso));
                    return (
                      <Box
                        key={iso}
                        onClick={() => setSelectedIso(iso)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          cursor: 'pointer',
                          py: 0.25,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            minHeight: 36,
                            p: 0,
                            boxSizing: 'border-box',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            textAlign: 'center',
                            fontSize: 14,
                            fontWeight: selected || isToday ? 700 : 500,
                            bgcolor: selected ? 'primary.main' : 'transparent',
                            color: selected ? 'primary.contrastText' : 'text.primary',
                            border: '1.5px solid',
                            borderColor: isToday && !selected ? 'primary.main' : 'transparent',
                            '&:hover': {
                              bgcolor: selected ? 'primary.main' : 'primary.lighter',
                            },
                          }}
                        >
                          <Box component='span' sx={{ display: 'block', lineHeight: 1 }}>
                            {day}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            mt: 0.35,
                            borderRadius: '50%',
                            bgcolor: selected
                              ? 'primary.main'
                              : oneDate
                                ? 'primary.main'
                                : 'secondary.main',
                            visibility: oneDate || weekly ? 'visible' : 'hidden',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
                <Stack direction='row' spacing={2} sx={{ mt: 1.5 }}>
                  <Stack direction='row' spacing={0.75} alignItems='center'>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                    <Typography variant='caption' color='text.secondary'>One date</Typography>
                  </Stack>
                  <Stack direction='row' spacing={0.75} alignItems='center'>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main' }} />
                    <Typography variant='caption' color='text.secondary'>Weekly rule</Typography>
                  </Stack>
                </Stack>
              </Box>

              <Box
                sx={{
                  flex: { md: '1 1 360px' },
                  width: { xs: '100%', md: 'auto' },
                  minWidth: 0,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                  {toLocalDate(selectedIso).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
                {selectedSlots.length === 0 ? (
                  <Typography
                    sx={{
                      mt: 2,
                      color: selectedDateRule || selectedWeekdayRule ? 'primary.main' : 'text.secondary',
                      fontWeight: selectedDateRule || selectedWeekdayRule ? 600 : 400,
                    }}
                  >
                    {selectedDateRule || selectedWeekdayRule
                      ? 'This day is disabled.'
                      : 'No slots on this day.'}
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1.5 }}>
                    <SlotCards slots={selectedSlots} usage={usageByDate[selectedIso]} />
                  </Box>
                )}
              </Box>
            </Stack>
          </Stack>

          <Stack spacing={1.5} sx={{ order: 3, mt: 4 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent='space-between'
              spacing={1}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tabs
                value={viewTab}
                onChange={(_event, value) => setViewTab(value)}
                sx={{ minHeight: 40 }}
              >
                <Tab label='Weekdays Timeslots' sx={{ textTransform: 'none', fontWeight: 700 }} />
                <Tab label='Specific Date Timeslots' sx={{ textTransform: 'none', fontWeight: 700 }} />
                <Tab label='Delivery Slots Settings' sx={{ textTransform: 'none', fontWeight: 700 }} />
              </Tabs>
              <Stack direction='row' spacing={1} sx={{ pb: 1, flexShrink: 0 }}>
                {viewTab === 0 ? (
                  <Button
                    variant='outlined'
                    onClick={openWeekdayForm}
                    sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                  >
                    Update Weekdays
                  </Button>
                ) : viewTab === 1 ? (
                  <Button
                    variant='contained'
                    onClick={openDateForm}
                    sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                  >
                    Add Specific Date
                  </Button>
                ) : null}
              </Stack>
            </Stack>

            {viewTab === 1 ? (
              <>
            <TableContainer>
              <Table size='medium'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>Date</TableCell>
                    <TableCell align='center'>Slots</TableCell>
                    <TableCell align='center'>Points</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDateRules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align='center'>
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedDateRules.map((rule) => {
                      const slots = rule.slots || [];
                      const rowCount = Math.max(slots.length, 1);
                      return slots.length === 0 ? (
                        <TableRow key={rule.id}>
                          <TableCell align='center'>{formatTableDate(String(rule.date))}</TableCell>
                          <TableCell align='center' colSpan={2}>
                            This day is disabled.
                          </TableCell>
                          <TableCell align='center'>
                            <DateRuleActions
                              onEdit={() => openDateEditor(rule)}
                              onDelete={() => handleDelete(rule.id, 'date')}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        slots.map((slot, index) => {
                          const isLastSlot = index === rowCount - 1;
                          const cellBorderSx = {
                            verticalAlign: 'middle' as const,
                            borderBottom: isLastSlot ? undefined : 'none',
                            py: 0.5,
                            '&&': { paddingLeft: '16px', paddingRight: '16px' },
                          };
                          return (
                          <TableRow key={`${rule.id}-${index}`}>
                            {index === 0 ? (
                              <TableCell
                                align='center'
                                rowSpan={rowCount}
                                sx={{ verticalAlign: 'middle', py: 0.5 }}
                              >
                                {formatTableDate(String(rule.date))}
                              </TableCell>
                            ) : null}
                            <TableCell align='center' sx={cellBorderSx}>
                              {String(slot.slot || '').replace(/\s+/g, ' ').trim()}
                            </TableCell>
                            <TableCell align='center' sx={cellBorderSx}>
                              <Box
                                component='span'
                                sx={{
                                  display: 'inline-block',
                                  minWidth: '4.5ch',
                                  textAlign: 'right',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {Number(slot.maxPoints) || 0}
                              </Box>
                            </TableCell>
                            {index === 0 ? (
                              <TableCell
                                align='center'
                                rowSpan={rowCount}
                                sx={{ verticalAlign: 'middle', py: 0.5 }}
                              >
                                <DateRuleActions
                                  onEdit={() => openDateEditor(rule)}
                                  onDelete={() => handleDelete(rule.id, 'date')}
                                />
                              </TableCell>
                            ) : null}
                          </TableRow>
                          );
                        })
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={sortedDateRules.length}
              page={datePageSafe}
              rowsPerPage={datesPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_event, page) => setDatePage(page)}
              onRowsPerPageChange={(event) => {
                setDatesPerPage(parseInt(event.target.value, 10));
                setDatePage(0);
              }}
            />
              </>
            ) : null}

            {viewTab === 0 ? (
              <Stack spacing={1.25}>
                {WEEKDAYS.map((day) => {
                  const rule = rules.weekdays
                    .filter((item) => Number(item.weekday) === Number(day.value))
                    .sort((left, right) =>
                      String(right.effectiveFrom).localeCompare(String(left.effectiveFrom))
                    )[0];
                  const isOverride = Boolean(rule);
                  const slots = isOverride ? rule?.slots || [] : defaultSlots;

                  return (
                    <Box
                      key={day.value}
                      sx={{
                        py: 1.25,
                        px: 1.75,
                        borderRadius: 2,
                        bgcolor: '#FDF0F1',
                        border: '1px solid',
                        borderColor: isOverride ? '#EE3750' : '#F8D3D8',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent='space-between'
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Stack direction='row' alignItems='center' spacing={1.5}>
                          <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#1b1b1f' }}>
                            {day.label}
                          </Typography>
                          {isOverride && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.2,
                                borderRadius: 1,
                                bgcolor: 'rgba(238, 55, 80, 0.1)',
                                color: '#EE3750',
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              Custom Override
                            </Box>
                          )}
                        </Stack>

                        <Stack direction='row' spacing={1} alignItems='center'>
                          {isOverride && rule && (
                            <Button
                              size='small'
                              variant='text'
                              color='error'
                              onClick={() => handleDelete(rule.id, 'weekday')}
                              sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, p: 0.5 }}
                            >
                              Reset to Default
                            </Button>
                          )}
                          <Button
                            size='small'
                            variant='outlined'
                            onClick={() => openWeekdayEditor(day.value)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: 12,
                              py: 0.35,
                              px: 1.25,
                              minHeight: 0,
                              borderColor: '#EE3750',
                              color: '#EE3750',
                              bgcolor: '#fff',
                              '&:hover': {
                                borderColor: '#d42d44',
                                bgcolor: '#fff',
                              },
                            }}
                          >
                            Update Slots
                          </Button>
                        </Stack>
                      </Stack>

                      <Box sx={{ mt: 0 }}>
                        {slots.length === 0 ? (
                          <Typography sx={{ color: 'error.main', fontWeight: 600, py: 0.5, fontSize: 13 }}>
                            This day is disabled (No delivery slots).
                          </Typography>
                        ) : (
                          <SlotCards slots={slots} pointsOnly />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : viewTab === 2 ? (
              <OrderGapSettings
                availableSlots={defaultSlots.map((s: SlotRow) => s.slot)}
                onSaved={onSaved}
              />
            ) : null}
          </Stack>

          <Dialog
            open={formOpen}
            onClose={closeForm}
            fullWidth
            maxWidth='md'
          >
            <DialogTitle>
              {mode === 'weekdays' ? 'Update Weekdays' : 'Add Specific Date'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                {mode === 'weekdays' ? (
                  <Box>
                  <Stack direction='row' spacing={1.25} flexWrap='wrap' useFlexGap>
                    {WEEKDAYS.map((day) => {
                      const selected = weekdays.includes(day.value);
                      return (
                        <Button
                          key={day.value}
                          aria-label={day.label}
                          onClick={() => selectWeekday(day.value)}
                          variant={selected ? 'contained' : 'outlined'}
                          color={selected ? 'primary' : 'inherit'}
                          sx={{
                            width: 48,
                            height: 48,
                            minWidth: 48,
                            p: 0,
                            borderRadius: '50%',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                            boxShadow: 'none',
                            borderColor: selected ? 'primary.main' : 'divider',
                            color: selected ? 'primary.contrastText' : 'text.primary',
                            bgcolor: selected ? 'primary.main' : 'transparent',
                            '&:hover': {
                              boxShadow: 'none',
                              bgcolor: selected ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        >
                          {day.short}
                        </Button>
                      );
                    })}
                  </Stack>
                  </Box>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label='Date'
                      value={date ? toLocalDate(date) : null}
                      minDate={(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return today;
                      })()}
                      inputFormat='dd/MM/yyyy'
                      onChange={(value: Date | null) => {
                        if (!value || Number.isNaN(value.getTime())) {
                          setDate('');
                          return;
                        }
                        selectDate(toIsoDate(value));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size='small' sx={{ width: 220 }} />
                      )}
                      PaperProps={{
                        sx: {
                          '& .MuiPickersDay-root': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            padding: 0,
                          },
                          '& .MuiPickersDay-today:not(.Mui-selected)': {
                            border: '1px solid',
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}

                {slotsOpen ? (
                  <Box sx={{ mt: 2 }}>
                    <DeliveryTimeSlotsEditor
                      key={`${mode}-${weekdays[0] ?? ''}-${date}-${editorKey}`}
                      value={draft}
                      onChange={handleDraftChange}
                      showTitle={false}
                      allowEmpty
                      onRemoveLast={() => {
                        setSlotsCleared(true);
                        toast.warning('This day is disabled. No delivery slots will be offered.');
                      }}
                    />
                    {slotsCleared ? (
                      <Typography sx={{ mt: 1, color: 'primary.main', fontWeight: 600 }}>
                        This day is disabled. No delivery slots will be offered.
                      </Typography>
                    ) : null}
                  </Box>
                ) : (
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                    {mode === 'weekdays'
                      ? 'Choose a weekday to edit the slots already used on that day.'
                      : 'Choose a date to edit the slots already used on that day.'}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button color='inherit' onClick={closeForm}>
                Cancel
              </Button>
              <LoadingButton
                variant='contained'
                loading={isSaving}
                disabled={!slotsOpen}
                onClick={handleSave}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(editingRule)}
            onClose={() => setEditingRule(null)}
            fullWidth
            maxWidth='md'
          >
            <DialogTitle>
              Edit {editingRule ? formatTableDate(String(editingRule.date)) : ''}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <DeliveryTimeSlotsEditor
                  key={editKey}
                  value={editDraft}
                  onChange={setEditDraft}
                  showTitle={false}
                  allowEmpty
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button color='inherit' onClick={() => setEditingRule(null)}>
                Cancel
              </Button>
              <LoadingButton variant='contained' loading={editSaving} onClick={handleEditSave}>
                Save
              </LoadingButton>
            </DialogActions>
          </Dialog>
      </Stack>
    </Card>
  );
};

export default DeliverySlotExceptions;

const DateRuleActions = ({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <Stack direction='row' alignItems='center' justifyContent='center' spacing={0.5}>
    <IconButton size='small' aria-label='edit' onClick={onEdit} sx={{ color: '#808080' }}>
      <Iconify icon='eva:edit-fill' sx={{ width: 20, height: 20 }} />
    </IconButton>
    <IconButton size='small' aria-label='delete' onClick={onDelete} sx={{ color: 'error.main' }}>
      <Iconify icon='eva:trash-2-outline' sx={{ width: 20, height: 20 }} />
    </IconButton>
  </Stack>
);
